import { replaceNullsWith } from "@/api/app";
import {
  useMutationCandidateChangeInfo,
  useQueryCandidateInfo,
} from "@/api/candidate/candidate";
import { keyFactory } from "@/api/keyFactory";
import { AddressFields } from "@/components/molecules/AddressForm/AddressFields";
import { PersonalInfoField } from "@/components/molecules/PersonalInfoForm/PersonalInfoFields";
import PersonalInformationForm from "@/components/molecules/PersonalInfoForm/PersonalInfoForm";
import {
  personalInfoDefaultValues,
  personalInfoSchema,
} from "@/components/molecules/PersonalInfoForm/PersonalInfoFormSchema";
import { ROUTE } from "@/constants/routes";
import { Button } from "@atoms/Button";
import { Form } from "@atoms/Form";
import { zodResolver } from "@hookform/resolvers/zod";
import AddressForm from "@molecules/AddressForm/AddressForm";
import {
  getAddressDefaultValues,
  optionalAddressSchema,
} from "@molecules/AddressForm/AddressFormSchema";
import ErrorDisplay from "@molecules/ErrorDisplay";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError, HttpStatusCode } from "axios";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import { RegisterContext } from "../RegisterContext";

const accountModificationSchema = z.object({
  address: optionalAddressSchema,
  personalInfo: personalInfoSchema,
});

const CheckInfo = () => {
  const queryClient = useQueryClient();
  const [errorMsg, setErrorMsg] = useState("");
  const { onNextStep, candidateId } = useOutletContext<RegisterContext>();

  // api calls

  const getCandidateQuery = useQueryCandidateInfo(candidateId, {
    staleTime: Infinity,
  });

  const changeCandidateInfoMutation = useMutationCandidateChangeInfo(
    candidateId,
    {
      onSuccess: async () => {
        setErrorMsg("");

        toast.success("Informations modifiées");

        queryClient.invalidateQueries({
          queryKey: keyFactory.candidate.info(),
        });
      },
      onError: (error) => {
        handleErrorCode(error);
      },
    }
  );

  // form

  const form = useForm<z.infer<typeof accountModificationSchema>>({
    resolver: zodResolver(accountModificationSchema),
    defaultValues: {
      address: getAddressDefaultValues(),
      personalInfo: personalInfoDefaultValues,
    },
  });

  const { data: candidateInfo } = getCandidateQuery;

  useEffect(() => {
    if (!candidateInfo) return;

    form.reset({
      address:
        replaceNullsWith(candidateInfo.address, "") || getAddressDefaultValues(),
      personalInfo:
        replaceNullsWith(candidateInfo.personalInfo, "") ||
        personalInfoDefaultValues,
    });
  }, [
    candidateInfo,
    candidateInfo?.personalInfo,
    candidateInfo?.address,
    form,
  ]);

  const handleErrorCode = (error: AxiosError) => {
    switch (error.status) {
      case HttpStatusCode.InternalServerError:
        setErrorMsg(
          "Le serveur a rencontré une erreur lors de la modification de vos informations."
        );
        return;

      default:
        setErrorMsg(
          "Une erreur est survenue lors de la modification de vos informations."
        );
        return;
    }
  };

  const onSubmit = (data: z.infer<typeof accountModificationSchema>) => {
    changeCandidateInfoMutation.mutate(data);
  };

  return (
    <div>
      <div className="flex p-4 flex-col items-start g-4 self-stretch rounded-md border-gray-200 bg-white border w-full">
        <h1>Vérifiez vos informations personnelles</h1>
        <span className="mb-2">
          Pour les modifier, allez dans{" "}
          <Link
            className="text-brand-700 hover:text-brand-500 font-medium"
            to={ROUTE.candidate.dashboard.profile()}
          >
            Mon profil
          </Link>
        </span>
        <span>
          L'adresse email de votre compte candidat est celle qui sera transmise
          à l'État dans le cadre de la télédéclaration.
        </span>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex w-full flex-col space-y-8"
          >
            <PersonalInformationForm
              hiddenFields={[
                PersonalInfoField.Email,
                PersonalInfoField.EmailConfirmation,
                PersonalInfoField.Password,
                PersonalInfoField.PasswordConfirmation,
              ]}
              disabledFields={Object.values(PersonalInfoField)}
              title=""
            />
            <AddressForm
              fieldName="address"
              disabledFields={Object.values(AddressFields)}
            />
            {errorMsg && <ErrorDisplay msg={errorMsg} />}
          </form>
        </Form>
      </div>
      <Button
        className="flex w-full font-semibold bg-brand-600 text-white mt-8"
        onClick={() => onNextStep(true)}
      >
        S'inscrire à une session d'examen
      </Button>
    </div>
  );
};

export default CheckInfo;
